import { useFormikContext } from "formik";
import React, { useRef, useState } from "react";
import { Permission } from "types";
import { useSwitchAccountLanguageConfirmationModal } from "./useSwitchAccountLanguageConfirmationModal";
import { IsGranted } from "@/components/Common/IsGranted";
import { LanguageSelector } from "@/components/Settings/LanguageSelector";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";

export interface LanguageFormData {
    accountLocale: string;
    locale?: string;
}

export const LanguageFormContent = () => {
    const seller = useSeller();
    const { values, setFieldValue } = useFormikContext<LanguageFormData>();
    const { SwitchLanguageConfirmationModal, confirmSwitchLanguage } = useSwitchAccountLanguageConfirmationModal();
    const originalLocale = useRef(values.accountLocale);
    const [newLanguage, setNewLanguage] = useState<string | undefined>(undefined);
    const supportedLanguages = seller?.preferences?.language?.supportedLanguages ?? [];
    const { isGranted } = useAuthorization();
    const isMultiLanguageEnabled = isGranted(Permission.MULTI_LANGUAGE_SUPPORT_VIEW);

    const getLanguageName = (locale: string): string | undefined => {
        const language = supportedLanguages.find((language) => language.locale === getBaseLocale(locale));
        return language?.name;
    };

    const originalLanguageName = useRef(getLanguageName(values.accountLocale));

    const handleLanguageSwitch = async (locale: string | undefined) => {
        if (!locale) return;

        const baseLocale = getBaseLocale(locale);
        const baseCurrent = getBaseLocale(values.accountLocale);
        const baseOriginal = getBaseLocale(originalLocale.current);
        const isLocaleTranslated = supportedLanguages.some((language) => language.locale === baseLocale);

        if (baseLocale === baseOriginal) {
            setFieldValue("accountLocale", locale);
            return;
        }

        if (isMultiLanguageEnabled && isLocaleTranslated && baseLocale !== baseCurrent) {
            setNewLanguage(getLanguageName(locale));
            const confirmation = await confirmSwitchLanguage();
            if (!confirmation) return;
        }

        setFieldValue("accountLocale", locale);
    };

    return (
        <div className="space-y-2">
            <IsGranted permissions={Permission.COMPANY_BASIC_INFO_VIEW}>
                <LanguageSelector
                    label="Account Language"
                    tooltip="This is the default language for this Xola account. By default, any user who logs into this Xola account will see this language displayed. This is also the default language that your customers will see when making bookings with this Xola account."
                    value={values.accountLocale}
                    onChange={async (value) => handleLanguageSwitch(value)}
                />
            </IsGranted>

            {seller.preferences.language.multiple && (
                <LanguageSelector
                    label="My Language"
                    tooltip="This is the default language for your specific Xola login. Changing this language will only affect what you see when you individually log in."
                    value={values.locale}
                    onChange={(value) => setFieldValue("locale", value)}
                />
            )}
            <SwitchLanguageConfirmationModal currentLanguage={originalLanguageName.current} newLanguage={newLanguage} />
        </div>
    );
};

const getBaseLocale = (locale: string) => locale.split("_")[0];
